import React, { useRef } from 'react';
import { Formik, Form } from 'formik';
import { Prompt, Redirect } from 'react-router-dom';
import {
  LINK_IN_BIO_SOCIAL_ERRORS,
  toLinkInBioSocialInput,
  toLinkInBioSocialUrl,
  type LinkInBio as LinkInBioValues,
  type LinkInBioPageSummary,
} from '@repo/lib';
import { UNSAVED_CHANGES_MESSAGE } from '@dashboard/components/UnsavedChanges';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { PageHeader } from '@dashboard/components/PageHeader';
import Link from '@dashboard/components/Link';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import useMountEffect from '@dashboard/hooks/useMountEffect';
import { useLinkInBio, useLinkInBioPages, useSaveLinkInBio } from '@dashboard/hooks/useLinkInBio';
import hasLinkInBioLab from '@dashboard/lib/linkInBioLab';
import BlocksSection from './components/BlocksSection';
import ThemeSection from './components/ThemeSection';
import { fromLinkInBioHomepage } from './homepage';
import LinkInBioPreview, { LinkInBioPreviewSheet } from './components/LinkInBioPreview';
import LinkInBioSkeleton from './components/LinkInBioSkeleton';
import LinkInBioTabs, { useLinkInBioTab } from './components/LinkInBioTabs';
import ProfileSection from './components/ProfileSection';
import PublishActions from './components/PublishActions';
import SocialsSection from './components/SocialsSection';
import YourLink from './components/YourLink';

function savedToast(wasPublished: boolean, isPublished: boolean) {
  if (isPublished) return wasPublished ? 'Changes published' : 'Published';
  return wasPublished ? 'Unpublished. Your changes are kept as a draft.' : 'Draft saved';
}

function useUnsavedChangesGuard() {
  const isDirty = useRef(false);

  useMountEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  });

  return isDirty;
}

const toFormValues = (linkInBio: LinkInBioValues): LinkInBioValues => ({
  ...linkInBio,
  socials: linkInBio.socials.map(social => ({
    ...social,
    url: toLinkInBioSocialInput(social.platform, social.url),
  })),
});

function validate(values: LinkInBioValues) {
  const socials = values.socials.map(social =>
    toLinkInBioSocialUrl(social.platform, social.url)
      ? undefined
      : { url: LINK_IN_BIO_SOCIAL_ERRORS[social.platform] },
  );
  return socials.some(Boolean) ? { socials } : {};
}

const byId = (summaries: LinkInBioPageSummary[] = []) =>
  Object.fromEntries(summaries.map(summary => [summary.id, summary]));

function Editor({
  initialValues,
  savedPages,
  upcomingEvents,
}: {
  initialValues: LinkInBioValues;
  savedPages: LinkInBioPageSummary[];
  upcomingEvents: LinkInBioPageSummary[];
}) {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const isDirty = useUnsavedChangesGuard();
  const save = useSaveLinkInBio();
  const tab = useLinkInBioTab();

  return (
    <Formik<LinkInBioValues>
      initialValues={toFormValues(initialValues)}
      enableReinitialize
      validate={validate}
      onSubmit={async (values, { resetForm }) => {
        const data = await save.mutateAsync(values).catch(() => null);
        if (!data) return;

        resetForm({ values: toFormValues(data.linkInBio) });
        toast({
          title: savedToast(initialValues.published, data.linkInBio.published),
          type: 'success',
        });

        if (!data.linkInBio.published && seller.homepage?.type === 'linkInBio') {
          const { error } = await updateSeller({
            homepage: fromLinkInBioHomepage(seller.homepage),
          });
          if (!error) {
            toast({
              title: 'Your link in bio is no longer your store homepage',
              type: 'success',
            });
          }
        }
      }}
    >
      {({ values, dirty, errors, submitCount }) => {
        isDirty.current = dirty;
        return (
          <WithPageSummaries blocks={values.blocks} savedPages={savedPages}>
            {({ pages, isLoadingPages }) => (
              <Form className="space-y-4 p-4">
                <Prompt
                  when={dirty}
                  message={next => next.pathname === '/link-in-bio' || UNSAVED_CHANGES_MESSAGE}
                />
                <PageHeader
                  title="Link in bio"
                  description={<YourLink savedPublished={initialValues.published} />}
                  topBarActions={<PublishActions savedPublished={initialValues.published} />}
                />
                <div className="grid gap-6 lg:grid-cols-12">
                  <div className="min-w-0 space-y-6 lg:col-span-7">
                    <LinkInBioTabs />
                    {tab !== 'socials' && errors.socials && submitCount > 0 && (
                      <p className="text-sm text-destructive">
                        A social link needs fixing before you can publish.{' '}
                        <Link to="/link-in-bio?tab=socials">Go to Social links</Link>
                      </p>
                    )}
                    {tab === 'profile' && <ProfileSection />}
                    {tab === 'socials' && <SocialsSection />}
                    {tab === 'theme' && <ThemeSection />}
                    {tab === 'blocks' && (
                      <BlocksSection
                        pages={pages}
                        upcomingEvents={upcomingEvents}
                        isLoadingPages={isLoadingPages}
                      />
                    )}
                  </div>
                  <div className="hidden lg:col-span-5 lg:block">
                    <div className="sticky top-4">
                      <LinkInBioPreview
                        pages={pages}
                        upcomingEvents={upcomingEvents}
                        isLive={initialValues.published}
                      />
                    </div>
                  </div>
                  <LinkInBioPreviewSheet pages={pages} upcomingEvents={upcomingEvents} />
                </div>
              </Form>
            )}
          </WithPageSummaries>
        );
      }}
    </Formik>
  );
}

function WithPageSummaries({
  blocks,
  savedPages,
  children,
}: {
  blocks: LinkInBioValues['blocks'];
  savedPages: LinkInBioPageSummary[];
  children?: (state: {
    pages: Record<string, LinkInBioPageSummary>;
    isLoadingPages: boolean;
  }) => React.ReactNode;
}) {
  const known = byId(savedPages);
  const missingIds = [
    ...new Set(
      (blocks ?? [])
        .map(block => (block.type === 'page' ? block.page : null))
        .filter((id): id is string => !!id && !known[id]),
    ),
  ];
  const { data, isLoading } = useLinkInBioPages(missingIds);

  return (
    <>
      {children?.({
        pages: { ...known, ...byId(data?.pages) },
        isLoadingPages: missingIds.length > 0 && isLoading,
      })}
    </>
  );
}

export default function LinkInBio() {
  const { seller, isAuthenticating } = useAuth();
  const hasLab = hasLinkInBioLab(seller);
  const { data, isLoading, isError } = useLinkInBio({ enabled: hasLab });

  if (!isAuthenticating && seller?._id && !hasLab) {
    return <Redirect to="/" />;
  }

  if (isAuthenticating || !seller?._id || isLoading) {
    return <LinkInBioSkeleton />;
  }

  if (isError || !data) {
    return (
      <div className="p-16 text-center text-sm text-muted-foreground">
        We could not load your link in bio. Refresh the page to try again.
      </div>
    );
  }

  return (
    <Editor
      initialValues={data.linkInBio}
      savedPages={data.pages}
      upcomingEvents={data.upcomingEvents}
    />
  );
}
